/**
 * Integrytas Group - Base App (MOBILE)
 * Pages: FXR.CURRENCY.LIST + FXR.RATE.LIST — mobile-native.
 *
 * Touch-first counterparts of src/pages/fxr/FxrReferencePages.tsx. Distinct
 * presentations (dense card list + facet chips + status pills + bottom-sheet
 * detail) over the SAME data/logic layer as the desktop pages:
 *   - reads via apiDb (fxr_currency + _tl, fxr_rate), resolving currency codes
 *     to translated names against the active locale (§17.B4),
 *   - the currency activate/deactivate write routes through the Common Routine
 *     (routedUpdate) — §2 single write path.
 * Never the desktop grid re-hosted in a phone viewport — §16. Rate rows are
 * read-only here (the desktop "Fetch latest rates" RPC is not a business write).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, Coins, ArrowLeftRight } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

interface TlRow {
  locale_code: string;
  display_text: string;
}

type StatusFacet = 'all' | 'active' | 'inactive';

/** Resolve a `_tl` set to the active locale, falling back to EN_US then any. */
function resolveTl(tls: TlRow[], activeLocale: string, fallback: string): string {
  const exact = tls.find((tl) => tl.locale_code === activeLocale);
  const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
  return exact?.display_text ?? fb?.display_text ?? fallback;
}

/* ────────────────────────── Currency Master ────────────────────────── */

interface MobileCurrencyRow {
  currency_code: string;
  symbol: string | null;
  decimal_places: number;
  is_active: 'Y' | 'N';
  display_text: string;
}

const CURRENCY_PAGE = 'FXR.CURRENCY.LIST';

export function MobileFxrCurrencyPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, CURRENCY_PAGE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileCurrencyRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileCurrencyRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('fxr_currency')
        .select('currency_code, symbol, decimal_places, is_active, fxr_currency_tl ( locale_code, display_text )')
        .order('sort_order');
      if (e) throw e;

      type RawCurrency = {
        currency_code: string;
        symbol: string | null;
        decimal_places: number;
        is_active: 'Y' | 'N';
        fxr_currency_tl?: TlRow[];
      };
      const mapped: MobileCurrencyRow[] = ((data ?? []) as RawCurrency[]).map((c) => ({
        currency_code: c.currency_code,
        symbol: c.symbol,
        decimal_places: c.decimal_places,
        is_active: c.is_active,
        display_text: resolveTl(c.fxr_currency_tl ?? [], activeLocale, c.currency_code),
      }));
      setRows(mapped);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (names follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return (
        r.currency_code.toLowerCase().includes(q) ||
        r.display_text.toLowerCase().includes(q) ||
        (r.symbol ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.fxr.currency.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  // CODE — Name: the ISO code is a natural, user-meaningful identifier (§17.B4).
  const codeName = (r: MobileCurrencyRow) => `${r.currency_code} — ${r.display_text}`;

  const subLine = (r: MobileCurrencyRow) => {
    const parts = [r.symbol?.trim() || null, t('mobile.fxr.currency.decimals', { count: r.decimal_places, defaultValue: `${r.decimal_places} decimals` })];
    return parts.filter(Boolean).join(' · ');
  };

  const toggleActive = async (r: MobileCurrencyRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'fxr_currency', 'currency_code', r.currency_code, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.fxr.currency.title', 'Currencies')}
        description={t('mobile.fxr.currency.description', 'Currencies available across the platform, with symbol and precision.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.fxr.currency.stat.total', 'Total') },
          { value: rows.filter((r) => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          { value: rows.filter((r) => r.is_active === 'N').length, label: t('common.boolean.inactive', 'Inactive') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.fxr.currency.search', 'Search currencies')}
          aria-label={t('mobile.fxr.currency.search', 'Search currencies')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.fxr.currency.none', 'No currencies match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.currency_code}
              lead={<Coins />}
              leadTone="blue"
              title={codeName(r)}
              subtitle={subLine(r)}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.fxr.currency.detail', 'Currency detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Coins size={20} /></span>
              <span>
                <div className="m-nm">{selected.display_text}</div>
                <div className="m-em">{selected.currency_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.fxr.currency.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.fxr.currency.symbol', 'Symbol')}
                value={selected.symbol?.trim() || t('common.none', 'None')}
              />
              <MobileDetailRow
                label={t('mobile.fxr.currency.decimal_places', 'Decimal places')}
                value={selected.decimal_places}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              <button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-primary')}
                disabled={actionBusy}
                onClick={canChange ? (() => void toggleActive(selected)) : undefined}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ────────────────────────── Currency Rates ────────────────────────── */

interface MobileRateRow {
  fxr_rate_id: number;
  base_currency_code: string;
  quote_currency_code: string;
  rate: number;
  effective_at: string;
  rate_source_code: string;
}

const RATE_FETCH_LIMIT = 500;

export function MobileFxrCurrencyRatePage({ session: _session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileRateRow[]>([]);
  const [currencyNames, setCurrencyNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [sourceFacet, setSourceFacet] = useState<string>('all');
  const [selected, setSelected] = useState<MobileRateRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('fxr_rate')
        .select('fxr_rate_id, base_currency_code, quote_currency_code, rate, effective_at, rate_source_code')
        .order('effective_at', { ascending: false })
        .limit(RATE_FETCH_LIMIT);
      if (e) throw e;
      setRows((data ?? []) as unknown as MobileRateRow[]);

      // Resolve currency codes to translated names (§17.B4 — never a bare code alone).
      const { data: rawCur, error: cErr } = await apiDb
        .from('fxr_currency')
        .select('currency_code, fxr_currency_tl ( locale_code, display_text )');
      if (cErr) throw cErr;

      type RawCurrency = { currency_code: string; fxr_currency_tl?: TlRow[] };
      const names: Record<string, string> = {};
      for (const c of (rawCur ?? []) as RawCurrency[]) {
        names[c.currency_code] = resolveTl(c.fxr_currency_tl ?? [], activeLocale, c.currency_code);
      }
      setCurrencyNames(names);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (names follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const nameOf = useCallback((code: string) => currencyNames[code] ?? code, [currencyNames]);
  // CODE — Name: the ISO code is a natural, user-meaningful identifier (§17.B4).
  const codeName = (code: string) => `${code} — ${nameOf(code)}`;

  const sources = useMemo(
    () => Array.from(new Set(rows.map((r) => r.rate_source_code))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (sourceFacet !== 'all' && r.rate_source_code !== sourceFacet) return false;
      if (!q) return true;
      return (
        r.base_currency_code.toLowerCase().includes(q) ||
        r.quote_currency_code.toLowerCase().includes(q) ||
        nameOf(r.base_currency_code).toLowerCase().includes(q) ||
        nameOf(r.quote_currency_code).toLowerCase().includes(q) ||
        r.rate_source_code.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, sourceFacet, nameOf]);

  const facets: { key: string; label: string }[] = [
    { key: 'all', label: t('mobile.fxr.rate.all', 'All') },
    ...sources.map((s) => ({ key: s, label: s })),
  ];

  const pairLabel = (r: MobileRateRow) => `${nameOf(r.base_currency_code)} → ${nameOf(r.quote_currency_code)}`;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.fxr.rate.title', 'Currency Rates')}
        description={t('mobile.fxr.rate.description', 'The latest exchange rates by source, newest first.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.fxr.rate.stat.total', 'Rates') },
          { value: sources.length, label: t('mobile.fxr.rate.stat.sources', 'Sources') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.fxr.rate.search', 'Search rates')}
          aria-label={t('mobile.fxr.rate.search', 'Search rates')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => sourceFacet === v}
        onSelect={(v) => setSourceFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.fxr.rate.none', 'No rates match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.fxr_rate_id}
              lead={<ArrowLeftRight />}
              leadTone="slate"
              title={pairLabel(r)}
              subtitle={`${r.rate.toFixed(6)} · ${formatDateTime(r.effective_at)}`}
              trailing={<MobileStatusPill tone="info">{r.rate_source_code}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.fxr.rate.detail', 'Rate detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ArrowLeftRight size={20} /></span>
              <span>
                <div className="m-nm">{pairLabel(selected)}</div>
                <div className="m-em">{selected.rate.toFixed(6)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.fxr.rate.base', 'From')} value={codeName(selected.base_currency_code)} />
              <MobileDetailRow label={t('mobile.fxr.rate.quote', 'To')} value={codeName(selected.quote_currency_code)} />
              <MobileDetailRow label={t('mobile.fxr.rate.rate', 'Rate')} value={selected.rate.toFixed(6)} />
              <MobileDetailRow label={t('mobile.fxr.rate.effective', 'Effective')} value={formatDateTime(selected.effective_at)} />
              <MobileDetailRow label={t('mobile.fxr.rate.source', 'Source')} value={selected.rate_source_code} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
